import React, { useContext } from 'react';
import { Button, Card, CardContent, Stack, Typography, Menu, MenuItem } from '@mui/material'
import { IReportAction } from '../../../../../shared/services/report-action/report-action.model';
import { Add, CheckCircleOutlined, EditOutlined, ReceiptLongOutlined, Visibility } from '@mui/icons-material';
import { EditEmissionDialog } from '../../../../dialogs/emissions/edit-emission-dialog';
import ReportActionService from '../../../../../shared/services/report-action/report-action.service';
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import { EmissionsActionCardHeading } from './emissions-action-card-heading';
import { ActionStatusChip } from './action-status-chip';
import { ActionStatusDialog } from './action-status-dialog';
import { formatEmissionAmount } from '../../../../../shared/utils/emission-summary-utils';
import { IUserDetails } from '../../../../../shared/services/user-details/user-details.model';
import { useNavigate } from 'react-router-dom';
export const EmissionsActionCard: React.FC<{
  reportAction: IReportAction,
  showActionInputHandler: (reportAction: IReportAction) => void,
  groupId: string,
  getGroup?: () => void,
  // Present only on bill-eligible groups (facilities, vehicles, purchased energy) with bill
  // extraction enabled; renders a third outstanding-action button that uploads a bill instead
  // of keying the data in.
  uploadBillHandler?: (reportAction: IReportAction) => void,
  status: 'outstanding' | 'completed' | 'upcoming'
  users: IUserDetails[],
}> = ({
  showActionInputHandler,
  groupId,
  reportAction,
  getGroup,
  uploadBillHandler,
  users,
  status,
}) => {

    const navigate = useNavigate();
    //Services
    const reportActionService = new ReportActionService()
    //Context
    const userNotificationsContext = useContext(UserNotificationsContext);
    const userDetailsContext = useContext(UserDetailsContext);
    const isAdmin = !!userDetailsContext.userDetails?.roles.includes('admin');
    //State
    const [openDialog, setDialogOpen] = React.useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
    const [editDialogEmissionId, setEditDialogEmissionId] = React.useState<string | null>(null);
    const [viewAnchorEl, setViewAnchorEl] = React.useState<null | HTMLElement>(null);
    const [editAnchorEl, setEditAnchorEl] = React.useState<null | HTMLElement>(null);

    const emissionIds = reportAction.emissionIds ?? [];
    const actionsBackLink = `/emissions/overview/group/${groupId}?tabId=1`;
    const actionsBackText = 'Return to tasks';

    const navigateToEmissionDetail = (emissionId: string) => {
      const params = new URLSearchParams();
      params.set('backLink', actionsBackLink);
      params.set('backText', actionsBackText);
      navigate(`/emissions/overview/${emissionId}?${params.toString()}`);
    };

    const emissionMenuLabel = (emissionId: string, index: number) => {
      if (reportAction.actionYear) {
        return `${reportAction.actionYear}${emissionIds.length > 1 ? ` (${index + 1})` : ''}`;
      }
      return `Emission ${index + 1}`;
    };

    const successHandler = () => {
      setDialogOpen(false);
      if (getGroup) {
        getGroup();
      }
    }

    const resumeHandler = () => {
      reportActionService.updateActionStatus(reportAction.id, null)
        .then(() => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
          if (getGroup) {
            getGroup();
          }
        })
        .catch(err => {
          console.log(err)
          userNotificationsContext.openSnackbar({ open: true, message: err?.response?.data?.message || 'There was an error, please try again', backgroundColor: 'text.primary' });
        })
    }

    const completeHandler = () => {
      // Completing without data means "done, nothing to add". Steering line for admins only:
      // the Not applicable option is admin-only in the status dialog.
      const message = isAdmin
        ? 'Are you sure you would like to record this task as completed without entering any data? If this task does not apply this period, use "I can\'t provide this" and choose Not applicable instead, so it is excluded rather than counted as done.'
        : 'Are you sure you would like to record this task as completed without entering any data?'
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            reportActionService.completeAction(reportAction.id)
              .then(() => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
                if (getGroup) {
                  getGroup();
                }
              })
              .catch(err => {
                console.log(err)
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              })
          }
        })
    }

    return (
      <Card sx={{ height: '100%', display: 'flex', boxShadow: 'none', borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', }}>
        <CardContent sx={{ position: 'relative', width: '100%' }}>
          <Stack sx={{ pr: 1, pl: 1, pt: 1, height: '100%' }}>
            <Stack sx={{
              position: 'relative',
              display: 'flex',
              flexGrow: 1,
              pb: 3,
              borderBottomStyle: status === 'upcoming' ? '' : 'solid',
              borderBottomColor: 'lightGrey.main',
              borderBottomWidth: '1px',
            }}>
              <EmissionsActionCardHeading
                reportAction={reportAction}
                status={status}
                completed={reportAction.completed}
                getGroup={getGroup}
                users={users}
              />
            </Stack>
            {status === 'outstanding' &&
              <Stack gap={1} sx={{ mt: 3 }}>
                {uploadBillHandler &&
                  <Button variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}
                    onClick={() => uploadBillHandler(reportAction)}>
                    <ReceiptLongOutlined />
                    <Typography sx={{ pt: 0.5, pb: 0.5 }}> Upload bill </Typography>
                  </Button>
                }
                <Stack display='flex' flexDirection='row' gap={1} alignItems='center' justifyContent='space-between'>
                  {/* Tasks created from readiness findings have no entity entry form; their fix
                      lives at the finding's deep link instead. */}
                  {reportAction.actionCode === ('readiness' as IReportAction['actionCode'])
                    ? <Button variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}
                      onClick={() => navigate(reportAction.deepLink || '/readiness')}>
                      <Visibility />
                      <Typography sx={{ pt: 0.5, pb: 0.5 }}> Open </Typography>
                    </Button>
                    : <Button variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}
                      onClick={() => showActionInputHandler(reportAction)}>
                      <Add />
                      <Typography sx={{ pt: 0.5, pb: 0.5 }}> Add data </Typography>
                    </Button>}
                  <Button variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}
                    onClick={() => completeHandler()}>
                    <CheckCircleOutlined />
                    <Typography sx={{ pt: 0.5, pb: 0.5 }}> Complete </Typography>
                  </Button>
                </Stack>
                {!reportAction.status &&
                  <Button variant='text' size='small' onClick={() => setStatusDialogOpen(true)} sx={{ alignSelf: 'center' }}>
                    <Typography variant='body2'>{"I can't provide this"}</Typography>
                  </Button>}
                {reportAction.status &&
                  <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
                    <ActionStatusChip status={reportAction.status} reason={reportAction.statusReason} />
                    <Button variant='text' size='small' onClick={resumeHandler}>
                      <Typography variant='body2'>Resume</Typography>
                    </Button>
                  </Stack>}
                <ActionStatusDialog
                  open={statusDialogOpen}
                  reportAction={reportAction}
                  closeHandler={() => setStatusDialogOpen(false)}
                  onSaved={() => { if (getGroup) { getGroup() } }}
                />
              </Stack>
            }
            {/* A not applicable task lands in the completed (resolved) list so the decision
                stays reviewable; Resume puts it back into outstanding work. */}
            {status === 'completed' && !reportAction.completed && reportAction.status === 'not_applicable' &&
              <Stack gap={1} sx={{ mt: 3 }}>
                <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
                  <ActionStatusChip status={reportAction.status} reason={reportAction.statusReason} />
                  <Button variant='text' size='small' onClick={resumeHandler}>
                    <Typography variant='body2'>Resume</Typography>
                  </Button>
                </Stack>
                {reportAction.statusReason &&
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}><b>Reason:</b> {reportAction.statusReason}</Typography>}
              </Stack>}
            {status === 'completed' && reportAction.completed &&
              <>
                {emissionIds.length > 0 && reportAction.reportedKgCo2e != null &&
                  <Stack direction='row' alignItems='baseline' justifyContent='space-between' sx={{ mt: 2 }}>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>Reported emissions</Typography>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>{formatEmissionAmount(reportAction.reportedKgCo2e)}</Typography>
                  </Stack>}
                {emissionIds.length > 0 ? (
                  <Stack display='flex' flexDirection='row' gap={1} alignItems='center' justifyContent='space-between' sx={{ mt: reportAction.reportedKgCo2e != null ? 1.5 : 3 }}>
                    {emissionIds.length === 1 ? (
                      <>
                        <Button onClick={() => { navigateToEmissionDetail(emissionIds[0]) }} variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}>
                          <Visibility />
                          <Typography sx={{ pt: 0.5, pb: 0.5 }}> View </Typography>
                        </Button>
                        <Button onClick={() => { setEditDialogEmissionId(emissionIds[0]); setDialogOpen(true) }} variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}>
                          <EditOutlined />
                          <Typography sx={{ pt: 0.5, pb: 0.5 }}> Edit </Typography>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={(e) => { setViewAnchorEl(e.currentTarget) }} variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}>
                          <Visibility />
                          <Typography sx={{ pt: 0.5, pb: 0.5 }}> View </Typography>
                        </Button>
                        <Menu
                          anchorEl={viewAnchorEl}
                          open={Boolean(viewAnchorEl)}
                          onClose={() => setViewAnchorEl(null)}
                        >
                          {emissionIds.map((emissionId, index) => (
                            <MenuItem key={emissionId} onClick={() => { setViewAnchorEl(null); navigateToEmissionDetail(emissionId) }}>
                              <Stack display='flex' flexDirection='row' gap={1} alignItems='center'>
                                <Visibility fontSize='small' />
                                {emissionMenuLabel(emissionId, index)}
                              </Stack>
                            </MenuItem>
                          ))}
                        </Menu>
                        <Button onClick={(e) => { setEditAnchorEl(e.currentTarget) }} variant='text' sx={{ borderColor: 'primary.main', borderStyle: 'solid', borderWidth: '1px', width: '100%' }}>
                          <EditOutlined />
                          <Typography sx={{ pt: 0.5, pb: 0.5 }}> Edit </Typography>
                        </Button>
                        <Menu
                          anchorEl={editAnchorEl}
                          open={Boolean(editAnchorEl)}
                          onClose={() => setEditAnchorEl(null)}
                        >
                          {emissionIds.map((emissionId, index) => (
                            <MenuItem key={emissionId} onClick={() => { setEditAnchorEl(null); setEditDialogEmissionId(emissionId); setDialogOpen(true); }}>
                              <Stack display='flex' flexDirection='row' gap={1} alignItems='center'>
                                <EditOutlined fontSize='small' />
                                {emissionMenuLabel(emissionId, index)}
                              </Stack>
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </Stack>
                ) : reportAction.dataDetected ? (
                  <Stack display='flex' flexDirection='row' gap={3} alignItems='center' sx={{ mt: 3 }}>
                    <Button disabled variant='text' sx={{ borderColor: 'success.main', borderStyle: 'solid', borderWidth: '1px', width: '100%', color: 'success.main' }}>
                      <Typography sx={{ pt: 0.5, pb: 0.5, color: 'success.main' }}> Done </Typography>
                    </Button>
                  </Stack>
                ) : (
                  <Stack display='flex' flexDirection='row' gap={3} alignItems='center' sx={{ mt: 3 }}>
                    <Button disabled variant='text' sx={{ borderColor: 'grey.400', borderStyle: 'solid', borderWidth: '1px', width: '100%', color: 'grey.500' }}>
                      <Typography sx={{ pt: 0.5, pb: 0.5 }}> Skipped </Typography>
                    </Button>
                  </Stack>)}
                {openDialog && editDialogEmissionId &&
                  <EditEmissionDialog
                    open={openDialog}
                    closeHandler={() => { setDialogOpen(false) }}
                    dialogDetailsId={editDialogEmissionId}
                    successHandler={successHandler}
                  />}
              </>}
          </Stack>
        </CardContent>
      </Card >
    )
  }

